import { Container } from 'pixi.js';

/** Zoom level for UI elements */
export const UI_ZOOM = 4;

/** Minimum allowed camera zoom */
export const MIN_ZOOM = 2;

/** Number of chunks to show in the larger dimension (ensures at least this many visible) */
export const VISIBLE_CHUNKS = 1.6;

/**
 * Client-only service for camera state.
 * Holds the camera entity reference and zoom level.
 */
export class CameraService {
  /**
   * The camera entity ID in the client world.
   */
  public cameraEntityId: number | undefined;

  /**
   * Current zoom level.
   */
  public zoom: number = MIN_ZOOM;

  /**
   * Current screen width in pixels.
   */
  public screenWidth: number = 0;

  /**
   * Current screen height in pixels.
   */
  public screenHeight: number = 0;

  /**
   * Update screen dimensions (call on resize and init).
   */
  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  /**
   * Update camera zoom based on viewport and chunk size.
   * Ensures the desired number of chunks (VISIBLE_CHUNKS) are visible in the larger dimension.
   *
   * @param chunkSize - Number of tiles per chunk
   * @param tileWidth - Width of a single tile in pixels
   */
  public updateZoom(chunkSize: number, tileWidth: number): void {
    const chunkWorldSize = chunkSize * tileWidth;
    const largestDim = Math.max(this.screenWidth, this.screenHeight);

    if (chunkWorldSize > 0 && largestDim > 0) {
      // Zoom = screen pixels / (visible chunks * pixels per chunk)
      const calculatedZoom = largestDim / (VISIBLE_CHUNKS * chunkWorldSize);
      this.zoom = Math.max(MIN_ZOOM, calculatedZoom);
    }
  }

  /**
   * Apply camera transform to a PixiJS container.
   * Centers the container on the given world position.
   *
   * @param container - The PixiJS container to transform
   * @param cameraX - Camera world X position (center point)
   * @param cameraY - Camera world Y position (center point)
   */
  public applyToContainer(container: Container, cameraX: number, cameraY: number): void {
    const screenCenterX = this.screenWidth / 2;
    const screenCenterY = this.screenHeight / 2;

    container.scale.set(this.zoom);
    // Round camera position to prevent jitter
    container.x = Math.round(screenCenterX - cameraX * this.zoom);
    container.y = Math.round(screenCenterY - cameraY * this.zoom);
  }

  /**
   * Reset the service (for testing).
   */
  public reset(): void {
    this.cameraEntityId = undefined;
    this.zoom = MIN_ZOOM;
    this.screenWidth = 0;
    this.screenHeight = 0;
  }
}
